import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import shoppingCartApi from '../../api/shoppingCart';

// 格式化时间数组为字符串
const formatCreateTime = (timeArray) => {
  if (!timeArray || !Array.isArray(timeArray) || timeArray.length < 6) return '-';
  
  const [year, month, day, hours, minutes, seconds] = timeArray;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ` +
         `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

// 分页查询购物车列表
export const fetchCartList = createAsyncThunk(
  'shoppingCart/fetchList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        page = 1, 
        pageSize = 10, 
        userId, 
        storeId, 
        goodsId, 
        startTime, 
        endTime, 
        sortField = 'create_time', 
        sortOrder = 'desc'
      } = params;

      const response = await shoppingCartApi.pageShoppingCarts({
        page,
        pageSize,
        userId,
        storeId,
        goodsId,
        startTime,
        endTime,
        sortField,
        sortOrder
      });
      
      // 处理响应数据
      const processedData = {
        records: (response.data && response.data.records ? response.data.records : []).map(cart => ({
          cartId: cart.cartId,
          username: cart.username,
          storeGroups: cart.storeGroups.map(storeGroup => ({
            storeId: storeGroup.storeId,
            storeName: storeGroup.storeName,
            cartItems: storeGroup.cartItems.map(item => ({
              cartItemId: item.cartItemId,
              goodsId: item.goodsId,
              goodsName: item.goodsName,
              goodsPrice: Number(item.goodsPrice),
              quantity: item.quantity,
              createTime: formatCreateTime(item.createTime)
            })),
            // 计算店铺总金额
            totalAmount: storeGroup.cartItems.reduce((total, item) => 
              total + (Number(item.goodsPrice) * item.quantity), 0)
          })),
          // 计算总商品数量
          totalItemCount: cart.storeGroups.reduce((total, storeGroup) => 
            total + storeGroup.cartItems.reduce((storeTotal, item) => storeTotal + item.quantity, 0), 0)
        })),
        total: response.data?.total || 0,
        current: response.data?.current || page,
        size: response.data?.size || pageSize,
        pages: response.data?.pages || 1
      };

      return processedData;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取购物车列表失败' });
    }
  }
);

// 获取购物车详情
export const fetchCartDetail = createAsyncThunk(
  'shoppingCart/fetchDetail',
  async (cartId, { rejectWithValue }) => {
    try {
      const response = await shoppingCartApi.getShoppingCartDetail(cartId);
      
      // 处理详情数据
      const processedDetail = {
        ...response.data,
        createTime: formatCreateTime(response.data.createTime),
        updateTime: formatCreateTime(response.data.updateTime)
      };

      return processedDetail;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取购物车详情失败' });
    }
  }
);

// 添加购物车
export const addShoppingCart = createAsyncThunk(
  'shoppingCart/add',
  async (shoppingCart, { rejectWithValue }) => {
    try {
      const response = await shoppingCartApi.addShoppingCart(shoppingCart);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '添加购物车失败' });
    }
  }
);

// 更新购物车
export const updateShoppingCart = createAsyncThunk(
  'shoppingCart/update',
  async ({ shoppingCartId, shoppingCart }, { rejectWithValue }) => {
    try {
      const response = await shoppingCartApi.updateShoppingCart(shoppingCartId, shoppingCart);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '更新购物车失败' });
    }
  }
);

// 删除购物车
export const deleteShoppingCart = createAsyncThunk(
  'shoppingCart/delete',
  async (shoppingCartId, { rejectWithValue }) => {
    try {
      const response = await shoppingCartApi.deleteShoppingCart(shoppingCartId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '删除购物车失败' });
    }
  }
);

const shoppingCartSlice = createSlice({
  name: 'shoppingCart',
  initialState: {
    cartList: [],
    cartDetail: null,
    total: 0,
    current: 1,
    pageSize: 10,
    loading: false,
    error: null
  },
  reducers: {},
  extraReducers: (builder) => {
    // 获取购物车列表
    builder.addCase(fetchCartList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchCartList.fulfilled, (state, action) => {
      state.loading = false;
      state.cartList = action.payload.records;
      state.total = action.payload.total;
      state.current = action.payload.current;
      state.pageSize = action.payload.size;
    });
    builder.addCase(fetchCartList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 获取购物车详情
    builder.addCase(fetchCartDetail.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchCartDetail.fulfilled, (state, action) => {
      state.loading = false;
      state.cartDetail = action.payload;
    });
    builder.addCase(fetchCartDetail.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 添加购物车
    builder.addCase(addShoppingCart.fulfilled, (state) => {
      // 可以在这里添加添加成功后的逻辑
    });

    // 更新购物车
    builder.addCase(updateShoppingCart.fulfilled, (state) => {
      // 可以在这里添加更新成功后的逻辑
    });

    // 删除购物车
    builder.addCase(deleteShoppingCart.fulfilled, (state) => {
      // 可以在这里添加删除成功后的逻辑
    });
  }
});

export default shoppingCartSlice.reducer; 